import React, { useEffect, useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Paper,
  TextField,
  InputAdornment,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Chip,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  CircularProgress,
  Grid,
  Tooltip,
  Avatar,
} from '@mui/material';
import {
  Search,
  Visibility,
  GetApp,
  Business,
  Person,
  Assessment,
} from '@mui/icons-material';
import {
  hostEarningsService,
  Host,
  HostEarningsFilters,
} from '@/services/hostEarningsService';
import { usePermissions } from '@/hooks/usePermissions';
import { HostDetailedEarningsModal } from '@/components/host/HostDetailedEarningsModal';

const formatCurrency = (amount: number): string => {
  return new Intl.NumberFormat('en-NG', {
    style: 'currency',
    currency: 'NGN',
    minimumFractionDigits: 0,
  }).format(amount);
};

const formatDate = (dateString?: string): string => {
  if (!dateString) return 'N/A';
  return new Date(dateString).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
};

export const HostEarningsBreakdown: React.FC = () => {
  const { can, isSuperAdmin } = usePermissions();
  const [hosts, setHosts] = useState<Host[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  const [filters, setFilters] = useState<HostEarningsFilters>({
    type: 'all',
    status: 'approved',
  });

  // View Details Dialog
  const [viewDialog, setViewDialog] = useState<{
    open: boolean;
    host: Host | null;
  }>({ open: false, host: null });

  // Detailed Earnings Dialog
  const [detailedEarningsDialog, setDetailedEarningsDialog] = useState<{
    open: boolean;
    hostId: string | null;
    hostName: string;
  }>({ open: false, hostId: null, hostName: '' });

  useEffect(() => {
    fetchHostEarnings();
  }, [page, rowsPerPage, filters]);

  const fetchHostEarnings = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await hostEarningsService.getHosts({
        ...filters,
        limit: rowsPerPage,
        offset: page * rowsPerPage,
      });
      setHosts(response.data.hosts);
      setTotal(response.data.pagination.total);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to load host earnings');
    } finally {
      setLoading(false);
    }
  };

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value);
  };

  const handleSearchSubmit = () => {
    setFilters({ ...filters, search: searchTerm });
    setPage(0);
  };

  const handleTypeChange = (event: any) => {
    setFilters({ ...filters, type: event.target.value });
    setPage(0);
  };

  const handleStatusChange = (event: any) => {
    setFilters({ ...filters, status: event.target.value });
    setPage(0);
  };

  const handlePageChange = (_event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleRowsPerPageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleExport = async () => {
    try {
      const blob = await hostEarningsService.exportHostEarnings(filters);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `host-earnings-${new Date().toISOString()}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err) {
      console.error('Export failed:', err);
    }
  };

  const handleViewDetails = (host: Host) => {
    setViewDialog({ open: true, host });
  };

  const handleCloseViewDialog = () => {
    setViewDialog({ open: false, host: null });
  };

  const handleViewDetailedEarnings = (host: Host) => {
    setDetailedEarningsDialog({
      open: true,
      hostId: host.id,
      hostName: host.name,
    });
  };

  const handleCloseDetailedEarnings = () => {
    setDetailedEarningsDialog({ open: false, hostId: null, hostName: '' });
  };

  // Filter hosts by search term (client-side for better UX)
  const filteredHosts = hosts.filter(host => {
    if (!searchTerm) return true;
    const search = searchTerm.toLowerCase();
    return (
      host.name.toLowerCase().includes(search) ||
      host.contact.email.toLowerCase().includes(search) ||
      host.id.toLowerCase().includes(search)
    );
  });

  return (
    <Box>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Filters */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={5}>
              <TextField
                fullWidth
                placeholder="Search by name, email, or ID..."
                value={searchTerm}
                onChange={handleSearchChange}
                onKeyPress={(e) => e.key === 'Enter' && handleSearchSubmit()}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <FormControl fullWidth>
                <InputLabel>Type</InputLabel>
                <Select value={filters.type} onChange={handleTypeChange} label="Type">
                  <MenuItem value="all">All Types</MenuItem>
                  <MenuItem value="individual">Individual</MenuItem>
                  <MenuItem value="fleet">Fleet</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select value={filters.status} onChange={handleStatusChange} label="Status">
                  <MenuItem value="all">All Status</MenuItem>
                  <MenuItem value="approved">Approved</MenuItem>
                  <MenuItem value="pending">Pending</MenuItem>
                  <MenuItem value="rejected">Rejected</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={3} display="flex" justifyContent="flex-end" gap={1}>
              <Button variant="outlined" startIcon={<GetApp />} onClick={handleExport}>
                Export CSV
              </Button>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Table */}
      <Card>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow sx={{ bgcolor: 'grey.100' }}>
                <TableCell>Host</TableCell>
                <TableCell>Type</TableCell>
                <TableCell>Contact</TableCell>
                <TableCell align="right">Total Earnings</TableCell>
                <TableCell align="center">Bookings</TableCell>
                <TableCell align="center">Vehicles</TableCell>
                <TableCell align="center">Drivers</TableCell>
                <TableCell align="center">Status</TableCell>
                <TableCell align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={9} align="center" sx={{ py: 4 }}>
                    <CircularProgress />
                  </TableCell>
                </TableRow>
              ) : filteredHosts.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={9} align="center" sx={{ py: 4 }}>
                    <Typography variant="body2" color="text.secondary">
                      No host earnings found
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                filteredHosts.map((host) => (
                  <TableRow key={host.id} hover>
                    <TableCell>
                      <Box display="flex" alignItems="center" gap={1.5}>
                        <Avatar
                          src={host.contact.profilePicture}
                          alt={host.name}
                          sx={{ width: 40, height: 40 }}
                        >
                          {host.firstName?.charAt(0)}
                        </Avatar>
                        <Box>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {host.name}
                          </Typography>
                          {host.branding.companyName && (
                            <Typography variant="caption" color="text.secondary">
                              {host.branding.companyName}
                            </Typography>
                          )}
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Chip
                        icon={host.type === 'fleet' ? <Business /> : <Person />}
                        label={host.type}
                        size="small"
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{host.contact.email}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {host.contact.phone}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body2" sx={{ fontWeight: 600, color: 'success.main' }}>
                        {formatCurrency(host.performance.totalEarnings)}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Typography variant="body2">{host.performance.totalBookings}</Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Tooltip title={`${host.assets.approvedVehicles} approved, ${host.assets.activeVehicles} active`}>
                        <Typography variant="body2">{host.assets.vehiclesCount}</Typography>
                      </Tooltip>
                    </TableCell>
                    <TableCell align="center">
                      <Typography variant="body2">{host.assets.driversCount}</Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Chip
                        label={host.status}
                        size="small"
                        color={
                          host.status === 'approved'
                            ? 'success'
                            : host.status === 'rejected'
                            ? 'error'
                            : 'warning'
                        }
                      />
                    </TableCell>
                    <TableCell align="center">
                      <Tooltip title="View Basic Details">
                        <IconButton size="small" onClick={() => handleViewDetails(host)}>
                          <Visibility fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Detailed Earnings Breakdown">
                        <IconButton
                          size="small"
                          onClick={() => handleViewDetailedEarnings(host)}
                          color="primary"
                        >
                          <Assessment fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={[5, 10, 25, 50]}
          component="div"
          count={total}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handlePageChange}
          onRowsPerPageChange={handleRowsPerPageChange}
        />
      </Card>

      {/* View Details Dialog */}
      <Dialog open={viewDialog.open} onClose={handleCloseViewDialog} maxWidth="md" fullWidth>
        <DialogTitle>Host Earnings Details</DialogTitle>
        <DialogContent>
          {viewDialog.host && (
            <Box sx={{ pt: 2 }}>
              <Grid container spacing={3}>
                {/* Host Info */}
                <Grid item xs={12}>
                  <Box display="flex" alignItems="center" gap={2} sx={{ mb: 2 }}>
                    <Avatar
                      src={viewDialog.host.contact.profilePicture}
                      alt={viewDialog.host.name}
                      sx={{ width: 60, height: 60 }}
                    >
                      {viewDialog.host.firstName?.charAt(0)}
                    </Avatar>
                    <Box>
                      <Typography variant="h6" sx={{ fontWeight: 600 }}>
                        {viewDialog.host.name}
                      </Typography>
                      {viewDialog.host.branding.companyName && (
                        <Typography variant="body2" color="text.secondary">
                          {viewDialog.host.branding.companyName}
                        </Typography>
                      )}
                      <Chip
                        icon={viewDialog.host.type === 'fleet' ? <Business /> : <Person />}
                        label={viewDialog.host.type}
                        size="small"
                        sx={{ mt: 0.5 }}
                      />
                    </Box>
                  </Box>
                </Grid>

                {/* Contact Info */}
                <Grid item xs={12} md={6}>
                  <Typography variant="caption" color="text.secondary">
                    Email
                  </Typography>
                  <Typography variant="body1">{viewDialog.host.contact.email}</Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="caption" color="text.secondary">
                    Phone
                  </Typography>
                  <Typography variant="body1">{viewDialog.host.contact.phone}</Typography>
                </Grid>

                {/* Status */}
                <Grid item xs={12} md={6}>
                  <Typography variant="caption" color="text.secondary">
                    Status
                  </Typography>
                  <Box sx={{ mt: 0.5 }}>
                    <Chip
                      label={viewDialog.host.status}
                      size="small"
                      color={
                        viewDialog.host.status === 'approved'
                          ? 'success'
                          : viewDialog.host.status === 'rejected'
                          ? 'error'
                          : 'warning'
                      }
                    />
                  </Box>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="caption" color="text.secondary">
                    Joined Date
                  </Typography>
                  <Typography variant="body1">{formatDate(viewDialog.host.dates.joinedAt)}</Typography>
                </Grid>

                {/* Performance Metrics */}
                <Grid item xs={12}>
                  <Box sx={{ bgcolor: 'success.lighter', p: 2, borderRadius: 1, mt: 2 }}>
                    <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600 }}>
                      Performance
                    </Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={6}>
                        <Typography variant="caption" color="text.secondary">
                          Total Earnings
                        </Typography>
                        <Typography variant="h6" sx={{ fontWeight: 700, color: 'success.main' }}>
                          {formatCurrency(viewDialog.host.performance.totalEarnings)}
                        </Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="caption" color="text.secondary">
                          Total Bookings
                        </Typography>
                        <Typography variant="h6" sx={{ fontWeight: 700 }}>
                          {viewDialog.host.performance.totalBookings}
                        </Typography>
                      </Grid>
                    </Grid>
                  </Box>
                </Grid>

                {/* Assets */}
                <Grid item xs={12}>
                  <Box sx={{ bgcolor: 'grey.100', p: 2, borderRadius: 1 }}>
                    <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600 }}>
                      Assets
                    </Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={6} sm={3}>
                        <Typography variant="caption" color="text.secondary">
                          Total Vehicles
                        </Typography>
                        <Typography variant="body1" sx={{ fontWeight: 600 }}>
                          {viewDialog.host.assets.vehiclesCount}
                        </Typography>
                      </Grid>
                      <Grid item xs={6} sm={3}>
                        <Typography variant="caption" color="text.secondary">
                          Approved
                        </Typography>
                        <Typography variant="body1" sx={{ fontWeight: 600 }}>
                          {viewDialog.host.assets.approvedVehicles}
                        </Typography>
                      </Grid>
                      <Grid item xs={6} sm={3}>
                        <Typography variant="caption" color="text.secondary">
                          Active
                        </Typography>
                        <Typography variant="body1" sx={{ fontWeight: 600 }}>
                          {viewDialog.host.assets.activeVehicles}
                        </Typography>
                      </Grid>
                      <Grid item xs={6} sm={3}>
                        <Typography variant="caption" color="text.secondary">
                          Drivers
                        </Typography>
                        <Typography variant="body1" sx={{ fontWeight: 600 }}>
                          {viewDialog.host.assets.driversCount}
                        </Typography>
                      </Grid>
                    </Grid>
                  </Box>
                </Grid>
              </Grid>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseViewDialog}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Detailed Earnings Modal */}
      <HostDetailedEarningsModal
        open={detailedEarningsDialog.open}
        hostId={detailedEarningsDialog.hostId}
        hostName={detailedEarningsDialog.hostName}
        onClose={handleCloseDetailedEarnings}
      />
    </Box>
  );
};
